import AirplaneTicketIcon from '@components/icons/airplane-ticket-icon';
import IconBusiness from '@components/icons/business';
import IconHome from '@components/icons/home';
import InRestaurantIcon from '@components/icons/in-restaurant-icon';
import IconLocation from '@components/icons/location';
import IconPerson from '@components/icons/person';
import {
  Maybe,
  useDebounce,
  useLagging,
  v2_AddressData,
  v2_BranchSuggestion,
  v2_useAddressDataFulltextSearch,
  v2_useAddressDataLocationSearch,
  v2_useAddressSuggestions,
  v2_useBranchSuggestions,
} from '@crocodille/common';
import { useGeolocation } from '@hooks/use-geolocation';
import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchAutocomplete from '../search-autocomplete/search-autocomplete';
import styles from './address-search.module.scss';

export interface AddressSearchProps {
  /**
   * Address data initially picked.
   */
  readonly pickedAddress?: Maybe<AddressSearchResult>;
  /**
   * Called when address is picked by user
   */
  onAddressPicked?: (address: Maybe<AddressSearchResult>) => void;

  /**
   * Container classes
   */
  readonly className?: string;

  /**
   * Label for the search input
   */
  readonly label?: string;

  /**
   * Query geolocation on mount
   */
  readonly queryGeolocation?: boolean;

  /**
   * Automatically select geolocation if available
   */
  readonly autoSelectGeolocation?: boolean;

  /**
   * Suggest saved addresses for user
   */
  readonly suggestSaved?: boolean;

  /**
   * Suggest branches and gates
   */
  readonly suggestBranches?: boolean;

  readonly isInputFocused?: boolean;
}

function createSuggestionKey(suggestion: AddressSearchResult): string {
  if ('code' in suggestion) {
    return `branch-${suggestion.code}${suggestion.gate}`;
  } else {
    return `address-${suggestion.id}`;
  }
}

function createSuggestionLabel(suggestion: AddressSearchResult): string {
  if ('code' in suggestion) {
    return suggestion.name;
  } else {
    return suggestion.label;
  }
}

function getSuggestionIcon(suggestion: AddressSearchResult): React.ReactNode {
  switch (suggestion.role) {
    case 'saved_address':
      if ('type' in suggestion) {
        switch (suggestion.type) {
          case 'home':
            return (
              <>
                <IconHome />
              </>
            );
          case 'work':
            return (
              <>
                <IconBusiness />
              </>
            );
        }
      }

      return (
        <>
          <IconPerson />
        </>
      );
    case 'customer_address':
      return (
        <>
          <IconLocation />
        </>
      );
    case 'branch':
      return (
        <>
          <InRestaurantIcon />
        </>
      );
    case 'gate':
      return (
        <>
          <AirplaneTicketIcon />
        </>
      );
    default:
      return null;
  }
}

export type AddressSearchResult =
  | (v2_AddressData & {
      readonly role?: 'saved_address' | 'customer_address';
      readonly location?: readonly [number, number];
    })
  | (v2_BranchSuggestion & {
      readonly role: 'branch' | 'gate' | 'train_station';
    });

const AddressSearch: React.FC<React.PropsWithChildren<AddressSearchProps>> = ({
  pickedAddress,
  onAddressPicked,
  className,
  label,
  queryGeolocation = false,
  autoSelectGeolocation = false,
  suggestSaved = false,
  suggestBranches = false,
  isInputFocused = false,
}) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState('');
  const [geolocationRetrieved, setGeolocationRetrieved] = useState(false);

  useEffect(() => void onAddressPicked?.(null), [onAddressPicked, searchText]);

  const debouncedSearchText = useDebounce(searchText, 200);

  const { data: locationData, isValidating: locationIsPending } =
    useGeolocation({
      query: queryGeolocation,
      enableHighAccuracy: true,
    });

  useEffect(() => {
    if (locationData && autoSelectGeolocation) {
      setGeolocationRetrieved(true);
    }
  }, [locationData, autoSelectGeolocation]);

  const selectGeolocation = autoSelectGeolocation && geolocationRetrieved;

  const {
    data: locationSearchAddress,
    isValidating: isValidatingLocationSearchAddresses,
  } = v2_useAddressDataLocationSearch(locationData?.coords ?? null);

  const {
    data: fulltextSearchAddressesData,
    isValidating: isValidatingFulltextSearchAddresses,
  } = v2_useAddressDataFulltextSearch({
    query: debouncedSearchText,
  });

  const fulltextSearchAddresses = useLagging(
    fulltextSearchAddressesData,
    Array.isArray,
  );

  const [pickedAddressData, setPickedAddressData] =
    useState<Maybe<AddressSearchResult>>(pickedAddress);

  useEffect(
    () => void onAddressPicked?.(pickedAddressData),
    [onAddressPicked, pickedAddressData],
  );

  const { data: userSuggestions, isValidating: isValidatingUserSuggestions } =
    v2_useAddressSuggestions(suggestSaved, searchText);

  const {
    data: branchSuggestions,
    isValidating: isValidatingBranchSuggestions,
  } = v2_useBranchSuggestions(suggestBranches ? searchText : false);

  const suggestions = useMemo(() => {
    let addresses: Extract<AddressSearchResult, { id: number }>[] = [
      ...(userSuggestions ?? []).map((a) => ({ ...a, role: 'saved_address' })),
      ...(fulltextSearchAddresses ?? []),
    ];

    if (locationSearchAddress && !selectGeolocation) {
      addresses = [
        { ...locationSearchAddress, role: 'customer_address' },
        ...addresses,
      ];
    }

    const addressIds = new Set();

    addresses = addresses.filter((address) => {
      if (addressIds.has(address.id)) {
        return false;
      }

      addressIds.add(address.id);

      return true;
    });

    const branches =
      branchSuggestions?.map?.((branch) => ({
        ...branch,
        role: branch.gate ? ('gate' as const) : ('branch' as const),
      })) ?? [];

    return [...branches, ...addresses];
  }, [
    locationSearchAddress,
    userSuggestions,
    fulltextSearchAddresses,
    branchSuggestions,
    selectGeolocation,
  ]);

  return (
    <div className={cn(styles.container, className)}>
      <SearchAutocomplete
        className={styles.search}
        componentId={'address-selector'}
        name={'addressSearch'}
        label={label || t('address_search.input_name')}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        onSuggestionPicked={(addressData) => setPickedAddressData(addressData)}
        createSuggestionKey={createSuggestionKey}
        createSuggestionLabel={createSuggestionLabel}
        getSuggestionIcon={getSuggestionIcon}
        suggestions={suggestions}
        busy={
          isValidatingLocationSearchAddresses ||
          isValidatingUserSuggestions ||
          isValidatingFulltextSearchAddresses ||
          isValidatingBranchSuggestions ||
          locationIsPending
        }
        selectSuggestion={
          selectGeolocation
            ? null
            : autoSelectGeolocation
            ? suggestions.find((s) => s.role === 'customer_address')
            : null
        }
        isInputFocused={isInputFocused}
      />
    </div>
  );
};

export default AddressSearch;
